import { Button } from '@/components/ui/button';
import BookingAuthStep from '@/components/booking/BookingAuthStep';
import BookingConfirmedStep from '@/components/booking/BookingConfirmedStep';
import BookingDateTimeStep from '@/components/booking/BookingDateTimeStep';
import BookingProfessionalStep from '@/components/booking/BookingProfessionalStep';
import BookingServicesStep from '@/components/booking/BookingServicesStep';
import BookingSummaryStep from '@/components/booking/BookingSummaryStep';
import { refreshAuthProfileCache } from '@/hooks/queries/auth.queries';
import { refreshProfileBookingsCache } from '@/hooks/queries/booking.queries';
import { useBookingAvailability } from '@/hooks/queries/useBookingAvailability';
import { useCompanyProfessionals } from '@/hooks/queries/useCompanyProfessionals';
import { useCompanyServices } from '@/hooks/queries/useCompanyServices';
import { useCreateBooking } from '@/hooks/queries/useCreateBooking';
import { useCreateWaitingList } from '@/hooks/queries/useCreateWaitingList';
import { useAuth } from '@/hooks/useAuth';
import { useBookingAvailabilityState } from '@/hooks/useBookingAvailabilityState';
import { useBookingPhoneVerification } from '@/hooks/useBookingPhoneVerification';
import { useBookingSelectionFlow } from '@/hooks/useBookingSelectionFlow';
import { getApiErrorMessage } from '@/lib/api/error';
import { getPhoneNumberDigits } from '@/lib/phoneNumber';
import { resolveCompanyAttendanceCapabilities } from '@/lib/company/capabilities';
import { canCompanyAcceptBookings } from '@/lib/company/status';
import type { ApiCompanyBySlug, ApiCompanyProfessionals } from '@/types/api';
import type { BookingExecutionMode, BookingSelectedTime, WaitingListSelection } from '@/types/booking-flow';
import type { BookingLocationState } from '@/types/booking-location-state';
import type { Service } from '@/types/domain/service';
import type { AuthLoginResponseDto } from '@/types/dto/auth.dto';
import { AuthService } from '@/services/auth.service';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CalendarCheck, Check, ListChecks } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildWaitingListSelection(date: Date, preferredTime: string | null): WaitingListSelection {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);

  const dayOfWeek = capitalize(
    normalizedDate.toLocaleDateString('pt-BR', {
      weekday: 'long',
    }),
  );

  return {
    date: normalizedDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    }),
    dayOfWeek,
    isoDate: normalizedDate.toISOString(),
    label: dayOfWeek,
    preferredTime,
  };
}

function buildCompanyAddress(company?: ApiCompanyBySlug) {
  if (!company) {
    return '';
  }

  const streetAndNumber = [company.street, company.number].filter(Boolean).join(', ');
  const cityAndState = [company.city?.name, company.city?.state?.stateCode].filter(Boolean).join(' - ');

  return [streetAndNumber, company.neighborhood, cityAndState].filter(Boolean).join(' · ');
}

interface BookingFlowProps {
  company?: ApiCompanyBySlug;
  enabled?: boolean;
  initialState?: BookingLocationState | null;
  onExit?: () => void;
  onWalkInSelected?: (selectedServices: string[]) => void;
  professionals?: ApiCompanyProfessionals[];
  requireEntryModeSelection?: boolean;
  services?: Service[];
  variant?: 'embedded' | 'standalone';
}

export function BookingFlow(props: BookingFlowProps) {
  const model = useBookingFlowModel(props);
  const { enabled, variant, selectionFlow, goBack, servicesQuery, professionalsQuery } = model;
  const catalogQuery =
    selectionFlow.step === 'services'
      ? servicesQuery
      : selectionFlow.step === 'professional'
        ? professionalsQuery
        : undefined;

  if (!enabled) {
    return null;
  }

  return (
    <div className={variant === 'standalone' ? 'bg-background min-h-screen' : 'space-y-5'}>
      <BookingFlowHeader {...model} />

      {variant === 'embedded' && selectionFlow.step !== 'confirmed' && selectionFlow.step !== 'auth' && (
        <div className="flex gap-1">
          {selectionFlow.progressSteps.map((progressStep, index) => (
            <div
              key={progressStep}
              className={`h-1 flex-1 rounded-full transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] ${index <= selectionFlow.activeProgressIndex ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
      )}

      {variant === 'embedded' && selectionFlow.step !== 'confirmed' && selectionFlow.currentStepIndex > 0 && (
        <button
          type="button"
          onClick={goBack}
          aria-label="Voltar"
          className="text-muted-foreground hover:text-foreground inline-flex min-h-11 items-center gap-2 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>
      )}

      <div
        className={
          variant === 'standalone'
            ? 'container mx-auto max-w-xl pt-6 pb-[calc(7rem+env(safe-area-inset-bottom))]'
            : 'w-full'
        }
      >
        {catalogQuery?.isLoading ? (
          <p role="status" className="text-muted-foreground py-6 text-center text-sm">
            Carregando opções de atendimento…
          </p>
        ) : catalogQuery?.isError ? (
          <div role="alert" className="bg-muted space-y-3 rounded-xl p-4 text-sm">
            <p>Não foi possível carregar as opções de atendimento.</p>
            <Button variant="outline" onClick={() => void catalogQuery.refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <BookingStepContent {...model} />
        )}
      </div>

      <BookingFlowActions {...model} />
    </div>
  );
}

function useBookingFlowModel({
  company,
  enabled = true,
  initialState = null,
  onExit,
  onWalkInSelected,
  professionals: externalProfessionals,
  requireEntryModeSelection = false,
  services: externalServices,
  variant = 'standalone',
}: BookingFlowProps) {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { isAuthenticated, setVerificationToken, signIn, user } = useAuth();

  const companyCapabilities = resolveCompanyAttendanceCapabilities(company);

  const hasBlockedUnavailableBooking = useRef(false);

  const authenticatedUserRef = useRef<AuthLoginResponseDto['user'] | null>(user);

  const initialExecutionMode: BookingExecutionMode = initialState?.bookingMode ?? 'booking';

  const [executionMode, setExecutionMode] = useState<BookingExecutionMode>(initialExecutionMode);

  const [waitingListSelection, setWaitingListSelection] = useState<WaitingListSelection | null>(
    initialState?.waitingListSelection ?? null,
  );

  const [waitingListPreferredTime, setWaitingListPreferredTime] = useState(
    initialState?.waitingListSelection?.preferredTime ?? '',
  );

  const [confirmedTime, setConfirmedTime] = useState<BookingSelectedTime | null>(null);

  const [isCompletingProfile, setIsCompletingProfile] = useState(false);

  const {
    companyId,
    services,
    professionals,
    initialSelectedServices,
    allowMultipleServicesPerBooking,
    shouldAskEntryMode,
    servicesQuery,
    professionalsQuery,
  } = useBookingCatalog({
    company,
    enabled,
    initialState,
    professionals: externalProfessionals,
    services: externalServices,
    requireEntryModeSelection,
  });
  const { createBooking, isPending: isCreatingBooking } = useCreateBooking();
  const { createWaitingList, isPending: isCreatingWaitingList } = useCreateWaitingList();

  const selectionFlow = useBookingSelectionFlow({
    allowMultipleServicesPerBooking,
    includeEntryModeStep: shouldAskEntryMode,
    initialSelectedServices,
    professionals,
    services,
  });

  const { setUserName, userName } = selectionFlow;

  const availabilityEnabled =
    enabled &&
    companyCapabilities.allowScheduledBookings &&
    selectionFlow.selectedServices.length > 0 &&
    selectionFlow.professionalId !== undefined;

  const {
    availabilityCursor,
    availabilityDays,
    handleAvailabilityDateSelect: handleAvailabilityDateSelectBase,
    handleLoadMoreAvailability,
    selectedAvailabilityDate,
    syncAvailability,
    today,
  } = useBookingAvailabilityState({
    availabilityEnabled,
    onClearSelectedTime: selectionFlow.clearSelectedTime,
    selectedProfessionalId: selectionFlow.selectedPro,
    selectedServices: selectionFlow.selectedServices,
    selectedTime: selectionFlow.selectedTime,
  });

  const {
    availability,
    isLoading: isLoadingAvailability,
    isFetching: isFetchingAvailability,
  } = useBookingAvailability(
    companyId,
    selectionFlow.selectedServices,
    selectionFlow.professionalId,
    availabilityCursor,
    availabilityEnabled,
  );

  useEffect(() => {
    syncAvailability(availability, availabilityCursor);
  }, [availability, availabilityCursor, syncAvailability]);

  useEffect(() => {
    if (typeof user?.name === 'string' && user.name.trim().length > 0 && userName.trim().length === 0) {
      // react-doctor-disable-next-line react-doctor/no-pass-data-to-parent -- setUserName belongs to useBookingSelectionFlow in this same component; auth profile arrives asynchronously.
      setUserName(user.name);
    }
  }, [setUserName, user?.name, userName]);

  // react-doctor-disable-next-line react-doctor/no-effect-event-handler -- Route and asynchronously fetched company status can change without a user event.
  useEffect(() => {
    if (!enabled || variant !== 'standalone' || !company || hasBlockedUnavailableBooking.current) {
      return;
    }

    if (!canCompanyAcceptBookings(company)) {
      hasBlockedUnavailableBooking.current = true;

      toast('Este local ainda não está recebendo agendamentos no Vira Tempo.', {
        description: 'Avise o responsável pelo WhatsApp ou tente outro local.',
      });

      navigate(`/${company.slug}`, { replace: true });
      return;
    }

    if (!companyCapabilities.allowScheduledBookings) {
      hasBlockedUnavailableBooking.current = true;

      toast('Este local não está recebendo agendamentos por aqui.', {
        description: companyCapabilities.allowWalkInQueue
          ? 'Entre na fila do local para continuar.'
          : 'Escolha outro local ou volte depois.',
      });

      navigate(`/${company.slug}`, { replace: true });
    }
  }, [
    company,
    companyCapabilities.allowScheduledBookings,
    companyCapabilities.allowWalkInQueue,
    enabled,
    navigate,
    variant,
  ]);

  const confirmedUserName =
    userName.trim() || (typeof user?.name === 'string' && user.name.trim().length > 0 ? user.name : '');

  const selectedAvailabilityDay = selectionFlow.selectedTime
    ? availabilityDays.find((day) => day.date === selectionFlow.selectedTime?.date)
    : undefined;

  const buildBookingPayload = () => {
    if (
      !company?.id ||
      !selectionFlow.professionalId ||
      !selectionFlow.selectedTime?.time ||
      !selectedAvailabilityDay?.isoDate ||
      selectionFlow.selectedServices.length === 0
    ) {
      return null;
    }

    return {
      companyId: company.id,
      date: selectedAvailabilityDay.isoDate,
      professionalId: selectionFlow.professionalId,
      services: selectionFlow.selectedServices,
      time: selectionFlow.selectedTime.time,
    };
  };

  const buildWaitingListPayload = () => {
    if (
      !company?.id ||
      !selectionFlow.professionalId ||
      !waitingListSelection?.isoDate ||
      selectionFlow.selectedServices.length === 0
    ) {
      return null;
    }

    return {
      companyId: company.id,
      date: waitingListSelection.isoDate,
      preferredTime: waitingListSelection.preferredTime,
      professionalId: selectionFlow.professionalId,
      serviceIds: selectionFlow.selectedServices,
    };
  };

  const submitBooking = async () => {
    const bookingPayload = buildBookingPayload();

    if (!bookingPayload) {
      toast.error('Não foi possível confirmar o agendamento', {
        description: 'Selecione novamente o serviço, o profissional e o horário antes de continuar.',
      });
      return false;
    }

    try {
      await createBooking(bookingPayload);
      setConfirmedTime(selectionFlow.selectedTime);
      await refreshProfileBookingsCache(queryClient, user?.id);
      return true;
    } catch (error) {
      toast.error('Não foi possível confirmar o agendamento', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
      return false;
    }
  };

  const submitWaitingList = async () => {
    const waitingListPayload = buildWaitingListPayload();

    if (!waitingListPayload) {
      toast.error('Não foi possível entrar na fila de espera', {
        description: 'Escolha novamente o dia e o profissional antes de continuar.',
      });
      return false;
    }

    try {
      await createWaitingList(waitingListPayload);
      return true;
    } catch (error) {
      toast.error('Não foi possível entrar na fila de espera', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
      return false;
    }
  };

  const phoneVerification = useBookingPhoneVerification({
    onAuthStepChange: selectionFlow.setAuthStep,
    onLinkedUserValidated: async () => {
      const hasCompletedAction = executionMode === 'waiting-list' ? await submitWaitingList() : await submitBooking();

      if (hasCompletedAction) {
        selectionFlow.setStep('confirmed');
      }
    },
    onProfileCompletionRequired: (authenticatedUser) => {
      authenticatedUserRef.current = authenticatedUser;
      const authenticatedUserName = authenticatedUser.name?.trim();

      if (authenticatedUserName) {
        selectionFlow.setUserName(authenticatedUserName);
      }
    },
    onVerificationTokenReceived: setVerificationToken,
    onVerificationSignIn: async (data) => {
      return signIn(data);
    },
    onVerificationCodeChange: selectionFlow.setVerificationCode,
    phoneNumber: selectionFlow.phoneNumber,
    verificationCode: selectionFlow.verificationCode,
  });

  const completeProfile = async () => {
    const authenticatedUser = authenticatedUserRef.current;
    const trimmedUserName = selectionFlow.userName.trim();

    if (!authenticatedUser || !trimmedUserName || isCompletingProfile) {
      return false;
    }

    setIsCompletingProfile(true);

    try {
      const updatedUser = await AuthService.updateProfile({
        fullName: trimmedUserName,
        phoneNumber: getPhoneNumberDigits(selectionFlow.phoneNumber) || authenticatedUser.phoneNumber || '',
      });

      await refreshAuthProfileCache(queryClient, updatedUser ?? authenticatedUser);
      return true;
    } catch (error) {
      toast.error('Não foi possível concluir seu cadastro', {
        description: getApiErrorMessage(error, 'Confira seu nome e tente novamente.'),
      });
      return false;
    } finally {
      setIsCompletingProfile(false);
    }
  };

  const goBack = () => {
    if (selectionFlow.currentStepIndex > 0) {
      selectionFlow.goPrevStep();
      return;
    }

    if (onExit) {
      onExit();
      return;
    }

    navigate(-1);
  };

  const handleAvailabilityDateSelect = (date?: Date) => {
    setExecutionMode('booking');
    setWaitingListSelection(null);
    setWaitingListPreferredTime('');
    handleAvailabilityDateSelectBase(date);
  };

  const handleSelectTime = (date: string, time: string, label: string) => {
    setExecutionMode('booking');
    setWaitingListSelection(null);
    setWaitingListPreferredTime('');
    selectionFlow.selectTime(date, time, label);
  };

  const handleToggleService = (serviceId: string) => {
    setExecutionMode('booking');
    setWaitingListSelection(null);
    setWaitingListPreferredTime('');
    selectionFlow.toggleService(serviceId);
  };

  const handleSelectProfessional = (professionalId: string) => {
    setExecutionMode('booking');
    setWaitingListSelection(null);
    setWaitingListPreferredTime('');
    selectionFlow.selectProfessional(professionalId);
  };

  const handleJoinWaitingList = (preferredTime: string | null) => {
    if (!selectedAvailabilityDate) {
      return;
    }

    const nextSelection = buildWaitingListSelection(selectedAvailabilityDate, preferredTime);

    setExecutionMode('waiting-list');
    setWaitingListPreferredTime(preferredTime ?? '');
    setWaitingListSelection(nextSelection);
    selectionFlow.clearSelectedTime();
    selectionFlow.setStep('summary');
  };

  const handleEntryModeSchedule = () => {
    setExecutionMode('booking');
    selectionFlow.setStep('professional');
  };

  const handleEntryModeWalkIn = () => {
    onWalkInSelected?.(selectionFlow.selectedServices);
  };

  const handleContinue = async () => {
    if (selectionFlow.step === 'summary' && isAuthenticated) {
      const hasCompletedAction = executionMode === 'waiting-list' ? await submitWaitingList() : await submitBooking();

      if (hasCompletedAction) {
        selectionFlow.setStep('confirmed');
      }
      return;
    }

    if (selectionFlow.step === 'auth') {
      const hasCompletedProfile = await completeProfile();

      if (!hasCompletedProfile) {
        return;
      }

      const hasCompletedAction = executionMode === 'waiting-list' ? await submitWaitingList() : await submitBooking();

      if (hasCompletedAction) {
        selectionFlow.goNextStep();
      }
      return;
    }

    selectionFlow.goNextStep();
  };

  const submitButtonLabel = useMemo(() => {
    if (selectionFlow.step === 'summary') {
      return executionMode === 'waiting-list' ? 'Entrar na fila de espera' : 'Confirmar agendamento';
    }

    if (selectionFlow.step === 'auth') {
      return executionMode === 'waiting-list' ? 'Confirmar e entrar' : 'Confirmar e agendar';
    }

    return 'Continuar';
  }, [executionMode, selectionFlow.step]);

  const isSubmitting = isCreatingBooking || isCreatingWaitingList || isCompletingProfile;
  return {
    company,
    enabled,
    variant,
    navigate,
    companyCapabilities,
    executionMode,
    waitingListSelection,
    waitingListPreferredTime,
    setWaitingListPreferredTime,
    confirmedTime,
    servicesQuery,
    professionalsQuery,
    services,
    professionals,
    allowMultipleServicesPerBooking,
    selectionFlow,
    availabilityDays,
    handleLoadMoreAvailability,
    selectedAvailabilityDate,
    today,
    availability,
    isLoadingAvailability,
    isFetchingAvailability,
    confirmedUserName,
    phoneVerification,
    goBack,
    handleAvailabilityDateSelect,
    handleSelectTime,
    handleToggleService,
    handleSelectProfessional,
    handleJoinWaitingList,
    handleEntryModeSchedule,
    handleEntryModeWalkIn,
    handleContinue,
    submitButtonLabel,
    isSubmitting,
  };
}

function BookingStepContent({
  company,
  navigate,
  companyCapabilities,
  executionMode,
  waitingListSelection,
  waitingListPreferredTime,
  setWaitingListPreferredTime,
  confirmedTime,
  services,
  professionals,
  allowMultipleServicesPerBooking,
  selectionFlow,
  availabilityDays,
  handleLoadMoreAvailability,
  selectedAvailabilityDate,
  today,
  availability,
  isLoadingAvailability,
  isFetchingAvailability,
  confirmedUserName,
  phoneVerification,
  handleAvailabilityDateSelect,
  handleSelectTime,
  handleToggleService,
  handleSelectProfessional,
  handleJoinWaitingList,
  handleEntryModeSchedule,
  handleEntryModeWalkIn,
}: Pick<
  ReturnType<typeof useBookingFlowModel>,
  | 'company'
  | 'navigate'
  | 'companyCapabilities'
  | 'executionMode'
  | 'waitingListSelection'
  | 'waitingListPreferredTime'
  | 'setWaitingListPreferredTime'
  | 'confirmedTime'
  | 'services'
  | 'professionals'
  | 'allowMultipleServicesPerBooking'
  | 'selectionFlow'
  | 'availabilityDays'
  | 'handleLoadMoreAvailability'
  | 'selectedAvailabilityDate'
  | 'today'
  | 'availability'
  | 'isLoadingAvailability'
  | 'isFetchingAvailability'
  | 'confirmedUserName'
  | 'phoneVerification'
  | 'handleAvailabilityDateSelect'
  | 'handleSelectTime'
  | 'handleToggleService'
  | 'handleSelectProfessional'
  | 'handleJoinWaitingList'
  | 'handleEntryModeSchedule'
  | 'handleEntryModeWalkIn'
>) {
  const stepContent = {
    services: (
      <BookingServicesStep
        allowMultipleServicesPerBooking={allowMultipleServicesPerBooking}
        companyName={company?.name ?? ''}
        onToggleService={handleToggleService}
        selectedServices={selectionFlow.selectedServices}
        services={services}
      />
    ),
    entryMode: (
      <section className="space-y-4">
        <div>
          <h2 className="text-foreground text-xl font-bold">Como quer ser atendido?</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Escolha entre agendar um horário e entrar na fila presencial.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={handleEntryModeSchedule}
            className="border-border bg-card hover:border-primary/40 rounded-xl border p-5 text-left transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
              <CalendarCheck className="h-5 w-5" />
            </span>
            <h3 className="text-card-foreground mt-4 text-lg font-semibold">Agendar horário</h3>
            <p className="text-muted-foreground mt-2 text-sm">Escolha profissional e horário.</p>
          </button>

          <button
            type="button"
            onClick={handleEntryModeWalkIn}
            className="border-border bg-card hover:border-primary/40 rounded-xl border p-5 text-left transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
              <ListChecks className="h-5 w-5" />
            </span>
            <h3 className="text-card-foreground mt-4 text-lg font-semibold">Fila presencial</h3>
            <p className="text-muted-foreground mt-2 text-sm">Entre na fila para atendimento no local.</p>
          </button>
        </div>
      </section>
    ),
    professional: (
      <BookingProfessionalStep
        onSelectProfessional={handleSelectProfessional}
        professionals={professionals}
        selectedProfessionalId={selectionFlow.selectedPro}
      />
    ),
    datetime: (
      <BookingDateTimeStep
        allowWaitingList={companyCapabilities.allowBookingWaitlist}
        availabilityDays={availabilityDays}
        hasMoreAvailability={Boolean(availability?.nextCursor)}
        isLoadingAvailability={isLoadingAvailability}
        onAvailabilityDateSelect={handleAvailabilityDateSelect}
        onJoinWaitingList={handleJoinWaitingList}
        onLoadMoreAvailability={() =>
          handleLoadMoreAvailability(availability?.nextCursor ?? null, isFetchingAvailability)
        }
        onSelectTime={handleSelectTime}
        onWaitingListPreferredTimeChange={setWaitingListPreferredTime}
        selectedAvailabilityDate={selectedAvailabilityDate}
        selectedTime={selectionFlow.selectedTime}
        today={today}
        waitingListPreferredTime={waitingListPreferredTime}
      />
    ),
    summary: (
      <BookingSummaryStep
        companyCoverPhoto={company?.coverPhoto ?? ''}
        companyName={company?.name ?? ''}
        companyRating={company?.rating}
        companyStreet={company?.street ?? ''}
        executionMode={executionMode}
        proName={selectionFlow.proName}
        selectedServiceItems={selectionFlow.selectedServiceItems}
        selectedTime={selectionFlow.selectedTime}
        totalDuration={selectionFlow.totalDuration}
        totalPrice={selectionFlow.totalPrice}
        waitingListSelection={waitingListSelection}
      />
    ),
    auth: (
      <BookingAuthStep
        authStep={selectionFlow.authStep}
        isRequestingCode={phoneVerification.isRequestingCode}
        isValidatingCode={phoneVerification.isValidatingCode}
        nameDescription={
          executionMode === 'waiting-list' ? 'Seu nome para a fila de espera' : 'Seu nome para o agendamento'
        }
        onAuthStepChange={selectionFlow.setAuthStep}
        onPhoneNumberChange={selectionFlow.setPhoneNumber}
        onRequestCode={phoneVerification.requestCode}
        onUserNameChange={selectionFlow.setUserName}
        onValidateCode={phoneVerification.validateCode}
        onVerificationCodeChange={selectionFlow.setVerificationCode}
        phoneRequestMethod={phoneVerification.requestedMethod}
        phoneNumber={selectionFlow.phoneNumber}
        userName={selectionFlow.userName}
        verificationCode={selectionFlow.verificationCode}
      />
    ),
    confirmed: (
      <BookingConfirmedStep
        companyAddress={buildCompanyAddress(company)}
        companyName={company?.name ?? ''}
        executionMode={executionMode}
        onGoHome={() => navigate('/')}
        proName={selectionFlow.proName}
        selectedTime={confirmedTime ?? selectionFlow.selectedTime}
        totalPrice={selectionFlow.totalPrice}
        userName={confirmedUserName}
        waitingListSelection={waitingListSelection}
      />
    ),
  } as const;
  return stepContent[selectionFlow.step];
}

function BookingFlowHeader({
  variant,
  executionMode,
  selectionFlow,
  goBack,
}: Pick<ReturnType<typeof useBookingFlowModel>, 'variant' | 'executionMode' | 'selectionFlow' | 'goBack'>) {
  return (
    <>
      {variant === 'standalone' && (
        <header className="border-border bg-background/95 sticky top-0 z-40 border-b backdrop-blur-sm">
          <div className="container flex min-h-16 items-center gap-3 py-2">
            {selectionFlow.step !== 'confirmed' && (
              <button
                type="button"
                onClick={goBack}
                aria-label="Voltar"
                className="hover:bg-muted -ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors"
              >
                <ArrowLeft className="text-foreground h-5 w-5" />
              </button>
            )}
            {selectionFlow.step === 'confirmed' && <div className="h-11 w-11 shrink-0" aria-hidden="true" />}
            <h1 className="text-foreground min-w-0 flex-1 text-center text-base font-semibold">
              {selectionFlow.step === 'confirmed'
                ? executionMode === 'waiting-list'
                  ? 'Fila de espera confirmada'
                  : 'Agendamento confirmado'
                : selectionFlow.stepLabels[selectionFlow.step]}
            </h1>
            <div className="h-11 w-11 shrink-0" aria-hidden="true" />
          </div>

          {selectionFlow.step !== 'confirmed' && selectionFlow.step !== 'auth' && (
            <div className="flex gap-1 px-4 pb-3">
              {selectionFlow.progressSteps.map((progressStep, index) => (
                <div
                  key={progressStep}
                  className={`h-1 flex-1 rounded-full transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] ${index <= selectionFlow.activeProgressIndex ? 'bg-primary' : 'bg-muted'}`}
                />
              ))}
            </div>
          )}
        </header>
      )}
    </>
  );
}

function BookingFlowActions({
  variant,
  selectionFlow,
  handleContinue,
  submitButtonLabel,
  isSubmitting,
}: Pick<
  ReturnType<typeof useBookingFlowModel>,
  'variant' | 'selectionFlow' | 'handleContinue' | 'submitButtonLabel' | 'isSubmitting'
>) {
  return (
    <>
      {selectionFlow.step !== 'confirmed' && selectionFlow.step !== 'entryMode' && (
        <div className={variant === 'standalone' ? 'sticky-bar' : 'border-border bg-background -mx-5 border-t p-5'}>
          <div className={variant === 'standalone' ? 'container mx-auto max-w-xl' : 'w-full'}>
            <button
              type="button"
              onClick={handleContinue}
              disabled={!selectionFlow.canContinue || isSubmitting}
              className="bg-primary text-primary-foreground flex min-h-14 w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-bold shadow-lg transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] hover:opacity-90 active:scale-[0.98] disabled:opacity-40"
            >
              <span>{isSubmitting ? 'Confirmando...' : submitButtonLabel}</span>
              {selectionFlow.step !== 'summary' && selectionFlow.step !== 'auth' && <ArrowRight className="h-5 w-5" />}
              {(selectionFlow.step === 'summary' || selectionFlow.step === 'auth') && <Check className="h-5 w-5" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function useBookingCatalog({
  company,
  enabled,
  initialState,
  professionals: externalProfessionals,
  services: externalServices,
  requireEntryModeSelection,
}: BookingFlowProps) {
  const companyCapabilities = resolveCompanyAttendanceCapabilities(company);
  const shouldUseInternalServices = externalServices === undefined;

  const shouldUseInternalProfessionals = externalProfessionals === undefined;

  const companyId =
    enabled && canCompanyAcceptBookings(company) && companyCapabilities.allowScheduledBookings
      ? company?.id
      : undefined;

  const internalServicesQuery = useCompanyServices(shouldUseInternalServices ? companyId : undefined);
  const internalServices = internalServicesQuery.services;

  const internalProfessionalsQuery = useCompanyProfessionals(
    shouldUseInternalProfessionals ? companyId : undefined,
    'SCHEDULE_ONLY',
  );
  const internalProfessionals = internalProfessionalsQuery.professionals;

  const services = externalServices ?? internalServices;

  const professionals = (externalProfessionals ?? internalProfessionals).filter(
    (professional) => professional.operationMode !== 'QUEUE_ONLY',
  );

  const initialServices = initialState?.selectedServices ?? [];

  const initialSelectedServices =
    initialState?.allowMultipleServicesPerBooking === false ? initialServices.slice(0, 1) : initialServices;

  const allowMultipleServicesPerBooking =
    company?.allowMultipleServicesPerBooking ?? initialState?.allowMultipleServicesPerBooking ?? true;

  const shouldAskEntryMode =
    requireEntryModeSelection && companyCapabilities.allowScheduledBookings && companyCapabilities.allowWalkInQueue;

  return {
    companyId,
    servicesQuery: internalServicesQuery,
    professionalsQuery: internalProfessionalsQuery,
    services,
    professionals,
    initialSelectedServices,
    allowMultipleServicesPerBooking,
    shouldAskEntryMode,
  };
}
